<template>
  <PageLayout>
    <template v-slot:header>
      <div class="ui padded grid">
        <div class="row">
          <div class="eight wide column">
            <Breadcrumb>
              <div class="active section">Chat IA</div>
            </Breadcrumb>
          </div>
          <div class="eight wide column header-actions right aligned">
            <span v-if="tokenUsage.total > 0" class="ui basic label token-usage-badge">
              <i class="microchip icon"></i>
              {{ formatNumber(tokenUsage.total) }} tokens usados nesta sessão
            </span>
          </div>
        </div>
      </div>
    </template>

    <template v-slot:main>
      <div class="chat-container">
        <div class="chat-messages" ref="messagesContainer">
          <div v-if="messages.length === 0" class="chat-empty">
            <i class="comment dots outline icon"></i>
            <p>Olá! Sou seu assistente de dados. Faça uma pergunta sobre os dados do sistema.</p>
            <div class="suggestions">
              <button
                v-for="suggestion in suggestions"
                :key="suggestion"
                class="ui basic label suggestion-btn"
                @click="sendSuggestion(suggestion)"
              >
                {{ suggestion }}
              </button>
            </div>
          </div>

          <div
            v-for="(msg, index) in messages"
            :key="index"
            class="message-row"
            :class="msg.role"
          >
            <div class="message-bubble">
              <div class="message-avatar">
                <i v-if="msg.role === 'user'" class="user circle icon"></i>
                <i v-else class="robot icon"></i>
              </div>
              <div class="message-content">
                <div class="message-text" v-html="formatMessage(msg.text)"></div>
                <div v-if="msg.sql" class="message-sql">
                  <button class="ui mini basic button" @click="toggleSql(index)">
                    <i class="code icon"></i>
                    {{ msg.showSql ? 'Ocultar SQL' : 'Ver SQL gerado' }}
                    <span v-if="msg.rowsCount != null" class="rows-badge">
                      {{ msg.rowsCount }} {{ msg.rowsCount === 1 ? 'registro' : 'registros' }}
                    </span>
                  </button>
                  <pre v-if="msg.showSql" class="sql-code">{{ msg.sql }}</pre>
                </div>
                <div v-if="msg.reportToken" class="message-report">
                  <button
                    class="ui mini primary basic button"
                    :class="{ loading: msg.downloadingReport }"
                    :disabled="msg.downloadingReport"
                    @click="downloadReport(index)"
                  >
                    <i class="download icon"></i> Baixar relatório (CSV)
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="loading" class="message-row assistant">
            <div class="message-bubble">
              <div class="message-avatar">
                <i class="robot icon"></i>
              </div>
              <div class="message-content">
                <div class="typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="chat-input-area">
          <div v-if="error" class="ui error message compact">
            <i class="exclamation triangle icon"></i> {{ error }}
          </div>
          <div class="chat-input-row">
            <div class="ui fluid input chat-input">
              <textarea
                ref="inputField"
                v-model="inputMessage"
                placeholder="Digite sua pergunta sobre os dados..."
                rows="1"
                :disabled="loading"
                @keydown.enter.exact.prevent="sendMessage"
                @input="autoResize"
              ></textarea>
            </div>
            <button
              class="ui primary button send-btn"
              :class="{ loading: loading }"
              :disabled="loading || !inputMessage.trim()"
              @click="sendMessage"
            >
              <i class="paper plane icon"></i>
            </button>
          </div>
          <span class="input-hint">Pressione Enter para enviar</span>
        </div>
      </div>
    </template>
  </PageLayout>
</template>

<script>
import PageLayout from '@/layouts/page_layout';
import Breadcrumb from '@/components/breadcrumb';
import Api from '@/resources/Api';

export default {
  name: 'AiChat',

  components: { PageLayout, Breadcrumb },

  data() {
    return {
      messages: [],
      inputMessage: '',
      loading: false,
      error: null,
      sessionId: null,
      tokenUsage: { input: 0, output: 0, total: 0 },
      suggestions: [
        'Quantos contratos ativos existem?',
        'Quais são os planos mais contratados?',
        'Qual o total de faturas em aberto?',
        'Quantos usuários foram cadastrados este mês?'
      ]
    };
  },

  mounted() {
    this.sessionId = this.generateSessionId();
  },

  methods: {
    generateSessionId() {
      return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    },

    async sendMessage() {
      const message = this.inputMessage.trim();
      if (!message || this.loading) return;

      this.error = null;
      this.inputMessage = '';
      this.$nextTick(() => this.resetInputHeight());

      this.messages.push({ role: 'user', text: message });
      this.loading = true;
      this.scrollToBottom();

      Api.aiChat.send({ message, session_id: this.sessionId })
        .then(response => {
          const { answer, sql, rows_count, report_token, token_usage } = response.data;
          this.messages.push({
            role: 'assistant',
            text: answer,
            sql: sql || null,
            rowsCount: rows_count != null ? rows_count : null,
            showSql: false,
            reportToken: report_token || null,
            downloadingReport: false
          });
          if (token_usage) {
            this.tokenUsage = token_usage;
          }
          this.$nextTick(() => this.scrollToBottom());
        })
        .catch(err => {
          const msg = err.response?.data?.error || 'Erro ao processar sua pergunta. Tente novamente.';
          this.error = msg;
          this.messages.push({
            role: 'assistant',
            text: 'Desculpe, ocorreu um erro ao processar sua pergunta.',
            sql: null,
            rowsCount: null,
            showSql: false,
            reportToken: null,
            downloadingReport: false
          });
          this.$nextTick(() => this.scrollToBottom());
        })
        .finally(() => {
          this.loading = false;
          this.$nextTick(() => this.$refs.inputField.focus());
        });
    },

    sendSuggestion(text) {
      this.inputMessage = text;
      this.sendMessage();
    },

    toggleSql(index) {
      this.messages[index].showSql = !this.messages[index].showSql;
      this.$forceUpdate();
    },

    downloadReport(index) {
      const msg = this.messages[index];
      if (!msg.reportToken || msg.downloadingReport) return;

      msg.downloadingReport = true;

      Api.aiChat.downloadReport(msg.reportToken)
        .then(response => {
          const blob = new Blob([response.data], {
            type: 'text/csv;charset=utf-8;'
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `relatorio-${msg.reportToken}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          this.error = 'Erro ao baixar o relatório. Tente novamente.';
        })
        .finally(() => {
          msg.downloadingReport = false;
        });
    },

    clearChat() {
      this.messages = [];
      this.error = null;
      this.sessionId = this.generateSessionId();
      this.tokenUsage = { input: 0, output: 0, total: 0 };
      this.$nextTick(() => this.$refs.inputField.focus());
    },

    formatNumber(value) {
      return Number(value || 0).toLocaleString('pt-BR');
    },

    formatMessage(text) {
      if (!text) return '';
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    autoResize(event) {
      const el = event.target;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    },

    resetInputHeight() {
      if (this.$refs.inputField) {
        this.$refs.inputField.style.height = 'auto';
      }
    }
  }
};
</script>

<style scoped>
.chat-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 140px);
  max-width: 900px;
  margin: 0 auto;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.chat-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #888;
  text-align: center;
  padding: 3rem 1rem;
}

.chat-empty i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #ccc;
}

.chat-empty p {
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
}

.suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  max-width: 600px;
}

.suggestion-btn {
  cursor: pointer;
  border-radius: 20px !important;
  padding: 0.5rem 1rem !important;
  font-size: 0.85rem !important;
  transition: background-color 0.2s;
}

.suggestion-btn:hover {
  background-color: #e8f4fd !important;
  border-color: #2185d0 !important;
  color: #2185d0 !important;
}

.message-row {
  display: flex;
}

.message-row.user {
  justify-content: flex-end;
}

.message-row.assistant {
  justify-content: flex-start;
}

.message-bubble {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  max-width: 80%;
}

.message-row.user .message-bubble {
  flex-direction: row-reverse;
}

.message-avatar i {
  font-size: 1.8rem;
  color: #999;
  margin-top: 2px;
}

.message-row.user .message-avatar i {
  color: #2185d0;
}

.message-content {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.message-text {
  padding: 0.75rem 1rem;
  border-radius: 12px;
  line-height: 1.5;
  font-size: 0.95rem;
  word-break: break-word;
}

.message-row.user .message-text {
  background-color: #2185d0;
  color: white;
  border-bottom-right-radius: 4px;
}

.message-row.assistant .message-text {
  background-color: #f4f4f4;
  color: #333;
  border-bottom-left-radius: 4px;
}

.message-sql {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.message-report {
  display: flex;
}

.rows-badge {
  display: inline-block;
  background: #e8f4fd;
  color: #2185d0;
  border-radius: 10px;
  padding: 0 6px;
  font-size: 0.75rem;
  margin-left: 6px;
}

.sql-code {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.8rem;
  overflow-x: auto;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0.75rem 1rem;
  background: #f4f4f4;
  border-radius: 12px;
  border-bottom-left-radius: 4px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #aaa;
  animation: bounce 1.2s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

.chat-input-area {
  border-top: 1px solid #e1e4e8;
  padding: 1rem;
  background: white;
}

.chat-input-row {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
}

.chat-input {
  flex: 1;
}

.chat-input textarea {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  resize: none;
  outline: none;
  font-family: inherit;
  line-height: 1.5;
  overflow-y: hidden;
  transition: border-color 0.2s;
}

.chat-input textarea:focus {
  border-color: #2185d0;
}

.chat-input textarea:disabled {
  background: #f9f9f9;
  cursor: not-allowed;
}

.send-btn {
  height: 44px !important;
  width: 44px !important;
  padding: 0 !important;
  border-radius: 8px !important;
  flex-shrink: 0;
}

.input-hint {
  display: block;
  font-size: 0.75rem;
  color: #aaa;
  margin-top: 0.3rem;
}

.header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.token-usage-badge {
  color: #888 !important;
  font-size: 0.8rem !important;
}

.token-usage-badge i.icon {
  color: #2185d0;
}
</style>
